import React, {
  useRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useMemo
} from "react";
import cx from "classnames";
import BaseTable, { AutoResizer } from "react-base-table";
import { FullScreen, useFullScreenHandle } from "react-full-screen";

import Loader from "../../loader";
import { useTable } from "../context";
import {
  setColumnsInitialWidth,
  selectSingleRow,
  toggleRowSelection,
  toggleRowExpansion,
  loadMoreDataStart,
  loadMoreData,
  loadMoreDataStop,
  updateStateValues,
  toggleSelectAll,
  onColumnResizeEnd
} from "../actions";
import {
  CLASS_PREFIX,
  getSelectionColumn,
  isFunction,
  isString,
  shouldReallySetInitialWidth
} from "../utils";
import TableHeaderCell from "./TableHeaderCell";
import SortIndicator from "./SortIndicator";
import ExpandIcon from "./ExpandIcon";
import HeaderRow from "./HeaderRow";
import CheckboxSelect from "./CheckboxSelect";
import RadioSelect from "./RadioSelect";
import LoadMoreLoader from "./LoadMoreLoader";
import Toolbar from "./Toolbar";

const Table = forwardRef((_, ref) => {
  const handleFullScreen = useFullScreenHandle();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const {
    tableId,
    enablePersistConfig,
    fireAction,
    data,
    columns,
    responsive,
    isInitialWidthSet,
    maxHeaderCount,
    isExpandable,
    isSelectable,
    multiSelect,
    uniqueDataKey,
    selectedRows,
    allRowsSelected,
    selectedRow,
    onRowClick,
    getRowClassName,
    disabledRows,
    controlSelection,
    hideHeader,
    isLoading,
    isLoadingMore,
    onLoadMore,
    loadedAll,
    fixSelectionColumn,
    estimatedRowHeight,
    rowRenderer,
    enableRowSpan,
    emptyRenderer,
    footerRenderer,
    footerHeight,
    sortBy,
    sortOrder,
    onSort,
    enableConfiguration,
    enableFullScreen,
    enableExportToCsv,
    toolbarChildren,
    // expand related props
    defaultExpandedRows,
    expandedRows,
    onRowExpand,
    controlExpansion
  } = useTable();

  const onFullScreenChange = useCallback(
    active => {
      setIsFullScreen(active);
    },
    [setIsFullScreen]
  );

  const handleResize = useCallback(
    dimensions => {
      if (
        !isInitialWidthSet &&
        !responsive &&
        shouldReallySetInitialWidth(columns, tableId, enablePersistConfig)
      ) {
        fireAction(setColumnsInitialWidth(dimensions.width));
      }
      fireAction(updateStateValues({ tableWidth: dimensions.width }));
    },
    [
      fireAction,
      isInitialWidthSet,
      columns,
      tableId,
      enablePersistConfig,
      responsive
    ]
  );

  /* istanbul ignore next */
  // We cannot test scroll events in jsdom env because id doesnt render the layout.
  // Hence we get zero offset for all elements
  const onScrollEndReached = useCallback(() => {
    if (isFunction(onLoadMore) && !isLoading && !isLoadingMore && !loadedAll) {
      fireAction(loadMoreDataStart());
      const promise = onLoadMore(data.length);
      if (promise instanceof Promise) {
        promise
          .then(d => {
            fireAction(loadMoreData(d));
          })
          .finally(() => {
            fireAction(loadMoreDataStop());
            if (allRowsSelected && !controlSelection) {
              fireAction(toggleSelectAll(allRowsSelected));
            }
          });
      }
    }
  }, [
    onLoadMore,
    fireAction,
    isLoading,
    isLoadingMore,
    loadedAll,
    data,
    allRowsSelected,
    controlSelection
  ]);

  const tableColumns = [
    isSelectable
      ? getSelectionColumn(
          multiSelect ? CheckboxSelect : RadioSelect,
          multiSelect,
          fixSelectionColumn
        )
      : null,
    ...columns
  ].filter(Boolean);

  // console.log(columns);

  const tableRef = useRef(null);

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (tableRef.current && data && data.length > 0) {
        tableRef.current.scrollToRow(0, "start");
      }
    },
    scrollToBottom: () => {
      if (tableRef.current && data && data.length > 0) {
        tableRef.current.scrollToRow(data.length, "end");
      }
    },
    scrollToRowIndex: (rowIndex, direction = "start") => {
      if (tableRef.current && data && data.length > 0) {
        tableRef.current.scrollToRow(rowIndex, direction);
      }
    },
    checkAllRows: () => {
      if (!controlSelection) {
        fireAction(toggleSelectAll(true));
      }
    },
    uncheckAllRows: () => {
      if (!controlSelection) {
        fireAction(toggleSelectAll(false));
      }
    }
  }));

  const shouldShowToolbar = useMemo(
    () =>
      enableFullScreen ||
      enableExportToCsv ||
      enableConfiguration ||
      toolbarChildren,
    [enableFullScreen, enableExportToCsv, enableConfiguration, toolbarChildren]
  );

  return (
    <AutoResizer>
      {({ width: w, height: h }) => {
        return (
          <FullScreen handle={handleFullScreen} onChange={onFullScreenChange}>
            <div
              style={
                isFullScreen
                  ? { height: "100vh" }
                  : { height: `${h}px`, width: `${w}px` }
              }
            >
              <AutoResizer onResize={handleResize}>
                {({ width, height }) => (
                  <>
                    {shouldShowToolbar && (
                      <Toolbar
                        width={width}
                        handleFullScreen={handleFullScreen}
                        isFullScreen={isFullScreen}
                        tableRef={tableRef}
                      />
                    )}
                    <BaseTable
                      ref={tableRef}
                      classPrefix={CLASS_PREFIX}
                      width={width}
                      height={
                        height -
                        (isFunction(footerRenderer) ? footerHeight : 0) -
                        (shouldShowToolbar ? 40 : 0)
                      }
                      rowKey={uniqueDataKey}
                      columns={tableColumns}
                      fixed={!responsive}
                      useIsScrolling
                      sortBy={{ key: sortBy, order: sortOrder }}
                      onColumnSort={sortObj => {
                        if (isFunction(onSort)) {
                          onSort(sortObj.key, sortObj.order);
                        }
                      }}
                      headerHeight={
                        hideHeader ? 0 : Array(maxHeaderCount).fill(40)
                      }
                      estimatedRowHeight={
                        enableRowSpan ? undefined : estimatedRowHeight
                      }
                      data={data}
                      components={{
                        SortIndicator,
                        ExpandIcon,
                        TableHeaderCell
                      }}
                      headerRenderer={props => (
                        <HeaderRow {...props} maxHeaderCount={maxHeaderCount} />
                      )}
                      onEndReachedThreshold={20}
                      onEndReached={onScrollEndReached}
                      rowProps={({ rowData, rowIndex }) => {
                        const rowId = rowData[uniqueDataKey];
                        return {
                          onClick: e => {
                            if (
                              e.target.classList.contains(
                                "oui-custom-control-label"
                              )
                            ) {
                              return;
                            }

                            if (
                              !e.rowId &&
                              !controlSelection &&
                              isSelectable &&
                              !disabledRows.includes(rowId)
                            ) {
                              if (multiSelect) {
                                fireAction(toggleRowSelection(rowId));
                              } else {
                                fireAction(selectSingleRow(rowId));
                              }
                            }

                            if (isFunction(onRowClick)) {
                              onRowClick(rowData, rowIndex);
                            }
                          }
                        };
                      }}
                      rowClassName={({ rowData, rowIndex }) => {
                        const rowId = rowData[uniqueDataKey];
                        let userClassName = enableRowSpan
                          ? `${CLASS_PREFIX}__rowSpan`
                          : "";

                        if (isFunction(getRowClassName)) {
                          userClassName += ` ${getRowClassName(
                            rowData,
                            rowIndex
                          )}`;
                        } else if (isString(getRowClassName)) {
                          userClassName += ` ${getRowClassName}`;
                        }

                        return cx(
                          {
                            [`${CLASS_PREFIX}__row--selected`]:
                              isSelectable &&
                              ((!multiSelect && selectedRow === rowId) ||
                                selectedRows.includes(rowId))
                          },
                          userClassName.trim()
                        );
                      }}
                      overlayRenderer={() => {
                        if (isLoading) {
                          return (
                            <div className="oui-h-100">
                              <Loader
                                isLoading
                                className="oui-h-100 data-grid-loading-overlay"
                              />
                            </div>
                          );
                        }

                        if (isLoadingMore) {
                          return <LoadMoreLoader />;
                        }

                        return null;
                      }}
                      emptyRenderer={
                        isFunction(emptyRenderer)
                          ? emptyRenderer
                          : () => emptyRenderer
                      }
                      footerHeight={
                        isFunction(footerRenderer) ? footerHeight : 0
                      }
                      footerRenderer={
                        isFunction(footerRenderer) ? footerRenderer : null
                      }
                      defaultExpandedRowKeys={defaultExpandedRows}
                      expandedRowKeys={expandedRows}
                      onRowExpand={p => {
                        if (!controlExpansion) {
                          const rowId = p.rowData[uniqueDataKey];
                          fireAction(toggleRowExpansion(rowId));
                        }

                        if (isFunction(onRowExpand)) {
                          onRowExpand(p);
                        }
                      }}
                      expandIconProps={obj => obj}
                      expandColumnKey={
                        isExpandable
                          ? isSelectable
                            ? tableColumns[1].key
                            : tableColumns[0].key
                          : null
                      }
                      overscanRowCount={enableRowSpan ? 20 : 1}
                      rowRenderer={
                        isFunction(rowRenderer)
                          ? ({ rowData, cells, rowIndex }) =>
                              rowRenderer(rowData, cells, rowIndex, columns)
                          : null
                      }
                      cellProps={({ column }) => {
                        return {
                          onClick: e => {
                            if (
                              enableRowSpan &&
                              column.key &&
                              column.key !== "__selection__"
                            ) {
                              e.stopPropagation();
                            }
                          }
                        };
                      }}
                      onColumnResizeEnd={column => {
                        fireAction(onColumnResizeEnd(column));
                      }}
                    />
                  </>
                )}
              </AutoResizer>
            </div>
          </FullScreen>
        );
      }}
    </AutoResizer>
  );
});

export default Table;
